import React, {useState, useEffect, useContext, createContext} from 'react';
import Socket from '../../Services/Socket';
import { usePreference } from '../../Data/Preference';
import { ulid } from 'ulid';
import API from '../../Services/API';
import { useVisibilityChange } from '../../Services/helpers';

const SocketContext = createContext();

export const useSocketContext = () => {
    return useContext(SocketContext);
};

let disconnectTimeout = null;
let reconnectInterval = null;

export default ({children}) => {

    const [subscriptions, setSubscriptions] = useState([]);
    const [channelId] = usePreference('socket-channel', ulid(), true);

    useEffect(() => {
        connectOrDisconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscriptions]);

    useEffect(() => {
        const onCloseUnsubscribe = Socket.onClose(() => {
            console.log('Socket closed. Reconnecting in 0.25 second');
            setTimeout(() => connectOrDisconnect(), 250);
        });
        const onErrorUnsubscribe = Socket.onError(() => {
            console.log('Socket errored. Reconnecting in 0.25 second');
            setTimeout(() => connectOrDisconnect(), 250);
        });

        reconnectInterval = setInterval(() => {
            connectOrDisconnect();
        }, 1000 * 60);

        return () => {
            onCloseUnsubscribe();
            onErrorUnsubscribe();
            clearInterval(reconnectInterval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useVisibilityChange(() => {
        connectOrDisconnect();
    });

    const connectOrDisconnect = () => {
        // Avoid disconnecting if we're just changing page but need to keep the subscription
        clearTimeout(disconnectTimeout);
        if (subscriptions.length === 0) {
            disconnectTimeout = setTimeout(() => Socket.disconnect(), 500);
        } else {
            Socket.connect();
        }
    }

    const subscribe = (sub, callback) => {
        const handler = (data) => {
            if (data.channelId !== channelId) {
                return;
            }
            if (sub.topic && data.topic !== sub.topic) {
                return;
            }
            if (sub.entity && data.entity?.entityId !== sub.entityId) {
                return;
            }

            callback(data);
        };

        const removeListener = Socket.onMessage(handler);

        setSubscriptions(prev => {
            return [...prev, JSON.stringify(sub)];
        });

        Socket.send({
            action: 'subscribe',
            channelId,
            jwt: API.getToken(),
            ...sub
        });

        return () => {
            Socket.send({
                action: 'unsubscribe',
                channelId,
                jwt: API.getToken(),
                ...sub
            });
            removeListener();
            setSubscriptions(prev => {
                return prev.filter(s => s !== JSON.stringify(sub));
            });
        }
    }

    const providerValues = {
        subscribe,
        getRecipient: (topic) => channelId+'_'+topic,
    };

    return <SocketContext.Provider value={providerValues}>
        {children}
    </SocketContext.Provider>
}
